import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { arrayIsShallowEqual, Datastore } from '@freelancer/datastore';
import { Enterprise, UsersCollection } from '@freelancer/datastore/collections';
import { Pwa } from '@freelancer/pwa';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount,
  startWith,
  switchMap,
} from 'rxjs/operators';
import {
  BLACKLISTED_IOS_FEATURES,
  Feature,
  FeatureFlagConfig,
  featureFlagConfigurations,
  SiteTheme,
} from './feature-flags.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private flagConfig$: Rx.Observable<FeatureFlagConfig>;
  private featureFlagBlacklist: Feature[] = [];
  featureFlagBlacklist$: Rx.Observable<Feature[]>;
  ready$: Rx.Observable<boolean>;

  constructor(
    private datastore: Datastore,
    private auth: Auth,
    private pwa: Pwa,
    private activatedRoute: ActivatedRoute,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {
    const { theme } = this.uiConfig;
    if (theme !== undefined && theme in featureFlagConfigurations) {
      this.flagConfig$ = Rx.of(featureFlagConfigurations[theme]);
    } else {
      const currentUserDoc = this.datastore.document<UsersCollection>(
        'users',
        this.auth.getUserId(),
      );
      // combine the status and value observables into an `Rx.Observable<User | undefined>`
      const loadedUser$ = currentUserDoc.status$.pipe(
        switchMap(status =>
          status.error ? Rx.of(undefined) : currentUserDoc.valueChanges(),
        ),
        distinctUntilChanged(
          (a, b) =>
            !!a && !!b && arrayIsShallowEqual(a.enterpriseIds, b.enterpriseIds),
        ),
      );

      this.flagConfig$ = this.auth.isLoggedIn().pipe(
        switchMap(loggedIn =>
          loggedIn
            ? loadedUser$.pipe(
                map(user => {
                  // Datastore errored, fallback to default theme
                  if (!user) {
                    return featureFlagConfigurations[SiteTheme.DEFAULT];
                  }

                  if (user.isDeloitteDcUser) {
                    return featureFlagConfigurations[SiteTheme.DELOITTE];
                  }

                  if (user.enterpriseIds?.includes(Enterprise.PMI)) {
                    return featureFlagConfigurations[SiteTheme.PMI];
                  }

                  if (user.enterpriseIds?.includes(Enterprise.YARA)) {
                    return featureFlagConfigurations[SiteTheme.YARA];
                  }

                  return featureFlagConfigurations[SiteTheme.DEFAULT];
                }),
              )
            : Rx.of(featureFlagConfigurations[SiteTheme.DEFAULT]),
        ),
      );
    }

    this.featureFlagBlacklist$ = this.activatedRoute.queryParamMap.pipe(
      map(queryParamMap => {
        const overrides = queryParamMap.getAll(
          'feature_flag_blacklist_overrides[]',
        ) as Feature[];
        // Merge the existing overrides with the new overrides from query param
        this.featureFlagBlacklist = [
          ...new Set(this.featureFlagBlacklist.concat(overrides)),
        ];
        return this.featureFlagBlacklist;
      }),
      distinctUntilChanged(),
    );

    this.ready$ = this.flagConfig$.pipe(
      map(() => true),
      startWith(false),
      distinctUntilChanged(),
      publishReplay(1),
      refCount(),
    );
  }

  getFlag(feature: Feature): Rx.Observable<boolean> {
    return this.featureFlagBlacklist$.pipe(
      switchMap(() =>
        this.isFeatureEnabled(feature)
          ? Rx.of(false)
          : this.flagConfig$.pipe(
              map(config => config[feature]),
              publishReplay(1),
              refCount(),
            ),
      ),
    );
  }

  isFeatureEnabled(feature: Feature): boolean {
    return (
      (this.pwa.isNative() &&
        this.pwa.getPlatform() === 'ios' &&
        BLACKLISTED_IOS_FEATURES.includes(feature)) ||
      this.featureFlagBlacklist.includes(feature)
    );
  }
}
